import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Phone, Mail, User, Check, Flag, Brain } from 'lucide-react';
import { useCampaignStore } from '../lib/store/campaignStore';

interface CandidateInfoStepProps {
  onNext: (candidate_info?: any) => void;
  onBack: () => void;
  progressPercentage: number;
}

export function CandidateInfoStep({ onNext, onBack, progressPercentage }: CandidateInfoStepProps) {
  const updateCampaignData = useCampaignStore(state => state.updateCampaignData);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    smsConsent: false,
    politicalParty: ''
  });
  const [shakeError, setShakeError] = useState(false);

  const politicalParties = [
    { id: 'democratic', name: 'Democratic', description: 'Democratic Party affiliation' },
    { id: 'republican', name: 'Republican', description: 'Republican Party affiliation' },
    { id: 'libertarian', name: 'Libertarian', description: 'Libertarian Party affiliation' },
    { id: 'green', name: 'Green Party', description: 'Green Party affiliation' },
    { id: 'independent', name: 'Independent', description: 'Independent political affiliation' },
    { id: 'nonpartisan', name: 'Nonpartisan', description: 'Nonpartisan campaign' }
  ];

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        if (!isStepValid()) {
          setShakeError(true);
          setTimeout(() => setShakeError(false), 500);
          return;
        }

        if (currentStep < 4) {
          setCurrentStep(prev => prev + 1);
        } else {
          handleNext();
        }
      }
    };

    document.addEventListener('keypress', handleKeyPress);
    return () => document.removeEventListener('keypress', handleKeyPress);
  }, [currentStep, formData]);

  const handleNext =async () => {
    if (!isStepValid()) {
      setShakeError(true);
      setTimeout(() => setShakeError(false), 500);
      return;
    }

    // Update global store with candidate info
    const candidate_info = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        smsConsent: formData.smsConsent,
        politicalParty: formData.politicalParty
      }
    await updateCampaignData({
      candidateInfo: candidate_info
    });
    onNext(candidate_info);
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    } else {
      onBack();
    }
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return !!formData.politicalParty;
      case 2:
        return formData.name.length >= 2;
      case 3:
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);
      case 4:
        return /^\d{10}$/.test(formData.phone.replace(/\D/g, '')) && formData.smsConsent;
      default:
        return false;
    }
  };

  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  const handleCheckboxClick = () => {
    setFormData(prev => ({ ...prev, smsConsent: !prev.smsConsent }));
  };

  const handlePartySelect = (partyId: string) => {
    setFormData(prev => ({ ...prev, politicalParty: partyId }));
    setTimeout(() => {
      setCurrentStep(prev => prev + 1);
    }, 200);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <h2 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8">What's your political affiliation?</h2>
            <p className="text-gray-400 mb-6 sm:mb-8">
              Select the party or affiliation that best describes your campaign.
            </p>
            <div className="grid gap-4">
              {politicalParties.map((party) => (
                <button
                  key={party.id}
                  onClick={() => handlePartySelect(party.id)}
                  className="p-4 rounded-lg text-left transition-colors bg-white/5 hover:bg-white/10 border-2 border-transparent hover:border-primary"
                >
                  <div className="font-semibold">{party.name}</div>
                  <div className="text-sm text-gray-400 mt-1">{party.description}</div>
                </button>
              ))}
            </div>
          </motion.div>
        );

      case 2:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <h2 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8">What's your name?</h2>
            <p className="text-gray-400 mb-6 sm:mb-8">
              Please enter your full legal name as it would appear on the ballot.
            </p>
            <div className="relative">
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter your full name"
                  className="w-full pl-10 pr-4 py-3 bg-white/10 rounded-lg text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#026fb9]"
                />
              </div>
            </div>
          </motion.div>
        );

      case 3:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <h2 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8">What's your email?</h2>
            <p className="text-gray-400 mb-6 sm:mb-8">
              We'll use this to send you important campaign information and updates.
            </p>
            <div className="relative">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="Enter your email address"
                  className="w-full pl-10 pr-4 py-3 bg-white/10 rounded-lg text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#026fb9]"
                />
              </div>
            </div>
          </motion.div>
        );

      case 4:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <h2 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8">What's your phone number?</h2>
            <p className="text-gray-400 mb-6 sm:mb-8">
              We'll use this to provide campaign support and updates.
            </p>
            <div className="space-y-6">
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => {
                    const formatted = formatPhoneNumber(e.target.value);
                    if (formatted.length <= 14) {
                      setFormData(prev => ({ ...prev, phone: formatted }));
                    }
                  }}
                  placeholder="(987) 654-3210"
                  className="w-full pl-10 pr-4 py-3 bg-white/10 rounded-lg text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#026fb9]"
                />
              </div>
              <div className="flex items-start gap-4">
                <button
                  type="button"
                  onClick={handleCheckboxClick}
                  className="relative flex-shrink-0 focus:outline-none"
                >
                  <div className={`w-7 h-7 border-2 rounded-lg transition-all duration-200 cursor-pointer hover:border-[#026fb9]/50 ${formData.smsConsent ? 'border-[#026fb9] bg-[#026fb9]' : 'border-white/20'}`}>
                    <motion.div
                      initial={false}
                      animate={formData.smsConsent ? {
                        scale: [0.5, 1.4, 1],
                        opacity: [0, 1],
                        transition: { duration: 0.3 }
                      } : {
                        scale: 0,
                        opacity: 0
                      }}
                      className="absolute inset-0 flex items-center justify-center"
                    >
                      <Check className="w-8 h-8 text-white transform -translate-y-[2px]" />
                    </motion.div>
                  </div>
                </button>
                <div className="flex-1">
                  <label className="text-sm text-gray-400 cursor-pointer select-none" onClick={handleCheckboxClick}>
                    I agree to receive campaign updates via SMS. Message and data rates may apply. 
                    You can opt out at any time by replying STOP.
                  </label>
                </div>
              </div>
            </div>

            <motion.button
              onClick={handleNext}
              className={`w-full mt-6 flex items-center justify-center gap-2 px-6 py-3 bg-[#026fb9] text-white rounded-lg hover:bg-[#026fb9]/90 transition-colors ${!isStepValid() ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={!isStepValid()}
              whileHover={{ scale: isStepValid() ? 1.02 : 1 }}
              whileTap={{ scale: isStepValid() ? 0.98 : 1 }}
            >
              <Brain className="w-5 h-5" />
              Generate Election Insights
            </motion.button>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-8">
      {renderStep()}

      <div className="flex justify-between pt-6">
        <button
          onClick={handleBack}
          className="flex items-center px-6 py-3 text-white hover:bg-white/10 rounded-lg transition-colors"
        >
          <ChevronLeft className="w-5 h-5 mr-2" />
          Back
        </button>
        {currentStep < 4 && (
          <motion.button
            onClick={() => setCurrentStep(prev => prev + 1)}
            animate={shakeError ? {
              x: [-10, 10, -10, 10, 0],
              transition: { duration: 0.4 }
            } : {}}
            className={`flex items-center px-6 py-3 bg-[#026fb9] text-white rounded-lg hover:bg-[#026fb9]/90 transition-colors ${!isStepValid() ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={!isStepValid()}
          >
            Next
            <ChevronRight className="w-5 h-5 ml-2" />
          </motion.button>
        )}
      </div>
    </div>
  );
}